/**
 * The shared 24-hour TCT date/time picker, on every page (imported by app.js). Use x-ui.datetime for a
 * labelled field, or mark any input yourself:
 *
 *     date + time:  <input type="text" data-tct-picker data-tct-min="2026-03-01 00:00" data-tct-max="2026-03-09 08:00">
 *     date only:    <input type="text" data-tct-picker="date">
 *     in a modal:   <input type="text" data-tct-picker data-tct-static>
 *
 * Marked inputs are wired on DOMContentLoaded; call window.tctPicker.attach() for inputs built later.
 *
 * Native <input type="date"> / <input type="datetime-local"> take their 12/24-hour format from the
 * browser's locale, so they render "07:00 p.m." for some users while every time the site prints is
 * 24-hour TCT. They also can't be given a time at all in date mode. Flatpickr is used everywhere instead.
 *
 * Values are plain wall-clock strings: "2026-03-09 08:00", or "2026-03-09" in date mode. TCT is UTC, so
 * nothing is ever converted between zones.
 */
window.tctPicker = (function () {
    const DATE_FORMAT = 'Y-m-d';
    const DATETIME_FORMAT = 'Y-m-d H:i';

    /**
     * Wire flatpickr onto an input and return the instance.
     *
     * The jQuery plugin is used rather than the bare flatpickr() function
     * because window.flatpickr is the ES module namespace object, not the
     * callable (see resources/js/bootstrap.js).
     */
    function attach(input, options) {
        options = options || {};
        const withTime = options.enableTime !== false;

        $(input).flatpickr($.extend({
            enableTime: withTime,
            time_24hr: true,
            dateFormat: withTime ? DATETIME_FORMAT : DATE_FORMAT,
            minuteIncrement: 1,
            allowInput: true,
            // Without this flatpickr hands off to the native picker on
            // phones, bringing the locale's AM/PM back with it.
            disableMobile: true,
        }, options));

        return input._flatpickr;
    }

    /**
     * Set an input's value whether or not a picker is attached yet, without
     * firing change handlers.
     */
    function setValue(input, value) {
        if (input._flatpickr) {
            input._flatpickr.setDate(value, false);
        } else {
            input.value = value;
        }
    }

    /**
     * The current TCT wall clock in picker format — used to stamp "now"
     * even on a page that has been open a while.
     */
    function nowUtc(withTime) {
        const now = new Date();
        const pad = function (value) { return String(value).padStart(2, '0'); };
        const date = now.getUTCFullYear() + '-' + pad(now.getUTCMonth() + 1) + '-' + pad(now.getUTCDate());

        return withTime === false ? date : date + ' ' + pad(now.getUTCHours()) + ':' + pad(now.getUTCMinutes());
    }

    /**
     * Format a Date's UTC wall clock the same way. Handy for presets built
     * from Date arithmetic.
     */
    function format(date, withTime) {
        const pad = function (value) { return String(value).padStart(2, '0'); };
        const formatted = date.getUTCFullYear() + '-' + pad(date.getUTCMonth() + 1) + '-' + pad(date.getUTCDate());

        return withTime === false ? formatted : formatted + ' ' + pad(date.getUTCHours()) + ':' + pad(date.getUTCMinutes());
    }

    return { attach: attach, setValue: setValue, nowUtc: nowUtc, format: format };
})();

document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('input[data-tct-picker]').forEach(function (input) {
        const options = { enableTime: input.dataset.tctPicker !== 'date' };

        if (input.dataset.tctMin) { options.minDate = input.dataset.tctMin; }
        if (input.dataset.tctMax) { options.maxDate = input.dataset.tctMax; }
        // Inside a Bootstrap modal the calendar has to render inline: appended
        // to <body> it sits outside the modal, whose focus trap pulls focus
        // back and closes it the moment a day is clicked.
        if (input.dataset.tctStatic !== undefined) { options.static = true; }

        window.tctPicker.attach(input, options);
    });
});
